import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ManufacturingSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const capabilities = [
    {
      title: 'Engineering & Production Team',
      description: '25+ specialized professionals across design, program management, quality assurance, production, and assembly teams.',
      image: 'https://static.readdy.ai/image/9cad1acab65e0b4933467abfa9c3db47/aa919cd2a604e2c9f2fed8b80304c5ad.jpeg',
      icon: 'ri-team-line',
    },
    {
      title: 'CAD Design & Manufacturing',
      description: 'Integrated CAD engineering and manufacturing capabilities supporting custom component development, fixture design, and precision tooling production.',
      image: 'https://static.readdy.ai/image/9cad1acab65e0b4933467abfa9c3db47/ed7dcb306db412caf5afeb1ea37a9091.jpeg',
      icon: 'ri-pencil-ruler-2-line',
    },
    {
      title: 'Precision Inspection Systems',
      description: 'High-precision inspection capability using Single Arm CMM – Romer measurement systems enabling detailed dimensional verification and quality validation.',
      image: 'https://static.readdy.ai/image/9cad1acab65e0b4933467abfa9c3db47/7914632e9b0f516c93276dacd4a14c43.jpeg',
      icon: 'ri-focus-3-line',
    },
    {
      title: 'Advanced Manufacturing Equipment',
      description: 'Production facilities equipped with industrial machining and fabrication equipment including CNC milling machines, VMC machining centers, EDM wire cutting systems, surface grinding machines, plano milling machines, and precision inspection tools.',
      image: 'https://static.readdy.ai/image/9cad1acab65e0b4933467abfa9c3db47/4c2d3c886e748b968d210c83bad3cc33.jpeg',
      icon: 'ri-settings-3-line',
    },
    {
      title: 'Global Industry Customers',
      description: 'Our partner facilities have supported projects for leading companies across automotive, engineering, and industrial manufacturing sectors.',
      image: 'https://static.readdy.ai/image/9cad1acab65e0b4933467abfa9c3db47/97bf8b3d298ef58febfb6bdcca6bb0ff.png',
      icon: 'ri-building-line',
    },
    {
      title: 'Precision Gauges & Tooling Fixtures',
      description: 'Specialized manufacturing of precision gauges, inspection fixtures, and tooling systems used for automotive and industrial component validation.',
      image: 'https://static.readdy.ai/image/9cad1acab65e0b4933467abfa9c3db47/00f8f98b45e185ab827a2f2a2fd367e6.jpeg',
      icon: 'ri-tools-line',
    },
  ];

  return (
    <section ref={ref} className="py-20 sm:py-28 bg-gray-50 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-primary mb-6">
            Partner Design & Manufacturing Capabilities
          </h2>
          <p className="text-base sm:text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Through our manufacturing partner network, True Passage also supports precision engineering, CAD design, tooling production, and industrial component design and production.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {capabilities.map((capability, index) => (
            <motion.div
              key={capability.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 + index * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group"
            >
              {/* Image */}
              <div className="relative h-64 w-full overflow-hidden">
                <img
                  src={capability.image}
                  alt={capability.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-primary/70 to-transparent"></div>
                
                {/* Icon */}
                <div className="absolute bottom-6 left-6 w-14 h-14 bg-gold-accent rounded-xl flex items-center justify-center">
                  <i className={`${capability.icon} text-2xl text-navy-primary`}></i>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 sm:p-8">
                <h3 className="text-lg sm:text-xl font-bold text-navy-primary mb-3">
                  {capability.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  {capability.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ManufacturingSection;